import { WorkflowTag, TagResponse, Tag, EntityType } from "@/generated";
import { FlowTag } from "./FlowTag";
import { FlowTagsRowMenu } from "./FlowTagsRowMenu";
import { TagActionsMenu } from "./TagActionsMenu";
import { Tooltip, TooltipContent, TooltipTrigger } from "../shadcn/tooltip";
import { useState } from "react";
import { usePermissions } from "@/hooks/usePermissions";
import { WORKSPACE_PERMISSIONS } from "@/constants/permissions";

type EntityTag = WorkflowTag | TagResponse | Tag;
type EditableTag = TagResponse | Tag;

export const FlowTagsStack = ({
  entityId,
  entityType,
  entityTags,
  disabled,
  onEditTag,
  onDeleteTag,
}: {
  entityId: string;
  entityType: EntityType;
  entityTags: EntityTag[];
  disabled?: boolean;
  onEditTag: (tag: EditableTag) => void;
  onDeleteTag: (tag: EditableTag) => void;
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { hasPermission } = usePermissions();
  const canEditTags = hasPermission(WORKSPACE_PERMISSIONS.EDIT, "group");

  const [actionsTagId, setActionsTagId] = useState<string>();
  const tagsEditable = canEditTags && !disabled;

  return (
    <div className="flex gap-1 items-center">
      {/* Horizontal Labels (max2) */}
      {entityTags.slice(0, 2).map((label) => {
        const key = "id" in label ? label.id : label.value;
        if (!tagsEditable || !("id" in label)) {
          return (
            <FlowTag
              key={String(key)}
              tag={label}
              setIsMenuOpen={setIsMenuOpen}
            />
          );
        }
        return (
          <span
            key={String(key)}
            className="relative"
            onContextMenu={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setActionsTagId(label.id);
            }}
          >
            <FlowTag tag={label} setIsMenuOpen={setIsMenuOpen} />
            <TagActionsMenu
              open={actionsTagId === label.id}
              onOpenChange={(open) =>
                setActionsTagId(open ? label.id : undefined)
              }
              onEdit={() => onEditTag(label)}
              onDelete={() => onDeleteTag(label)}
            >
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0"
              />
            </TagActionsMenu>
          </span>
        );
      })}
      {/* Remaining Labels in tooltip */}
      {entityTags.length > 2 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="shrink-0">
              <FlowTag
                tag={{
                  value: `+${entityTags.length - 2}`,
                  color: "#F5F5F5",
                }}
                setIsMenuOpen={setIsMenuOpen}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent
            className="bg-background shadow"
            side="bottom"
            align="center"
            hideArrow
          >
            <div className="flex flex-col gap-1 items-start px-1 py-1 rounded-md">
              {entityTags.slice(2, 10).map((label) => {
                const key = "id" in label ? label.id : label.value;
                return (
                  <FlowTag
                    key={String(key)}
                    tag={label}
                    setIsMenuOpen={setIsMenuOpen}
                  />
                );
              })}
              {entityTags.length > 10 && (
                <FlowTag
                  tag={{
                    value: `+${entityTags.length - 10}`,
                    color: "#B2DDFF",
                  }}
                  setIsMenuOpen={setIsMenuOpen}
                />
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      )}
      {tagsEditable && (
        <>
          <FlowTagsRowMenu
            entityId={entityId}
            entityType={entityType}
            entityTags={entityTags}
            isMenuOpen={isMenuOpen}
            setIsMenuOpen={setIsMenuOpen}
            onEditTag={onEditTag}
            onDeleteTag={onDeleteTag}
          />
        </>
      )}
    </div>
  );
};
